import { Alert } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { Fragment, useCallback, useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { translate } from 'react-switch-lang';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import { DeleteButtonIcon } from 'src/component/Buttons/DeleteButtonIcon';
import SweetAlert from 'sweetalert2';
import { useHttpClient } from '../../../../../../security/hooks/axiosProvider';
import Breadcrumb from '../../../../breadcrumb';
import styles from '../../../../styles/style-setting.module.scss';
import CreateDepartement from '../create-departement/create-departement';
import EditDepartement from '../edit-departement/edit-departement';
import ActivityColumns from './table-header/activity-columns';

// number of activities per page
const NUMBER_OF_DEPARTEMENTS_PER_PAGE = 15;

const ListDepartementsPage = (props) => {
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [departements, setDepartements] = useState([]);
  const httpClient = useHttpClient(true);
  const [pageNumber, setPageNumber] = useState(0);
  const activityPerPage = NUMBER_OF_DEPARTEMENTS_PER_PAGE;
  const changePage = ({ selected }) => {
    setPageNumber(selected);
  };
  const keycloak = useKeycloak().keycloak;
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);

  const [spinner, setSpinner] = useState({ state: false, id: '' });
  const pageCount = Math.ceil(
    (search === '' ? departements : departements.filter((activity) => activity.name.includes(search))).length /
      activityPerPage
  );
  const fetchCountries = () => {
    httpClient.current?.get(`/api/country/all`).then((response) => {
      setCountries(response.data);
    });
  };
  useEffect(() => {
    if (selectedCountry !== '') {
      httpClient.current?.get(`/api/region/all`).then((response) => {
        const regions = response.data.filter((region) => region.country.id == selectedCountry);
        setRegions(regions);
      });
    }
  }, [selectedCountry]);
  useEffect(() => {
    if (selectedCountry !== '') {
      getDepartements(selectedRegion, selectedCountry);
    } else {
      getDepartements();
    }
  }, [selectedRegion, selectedCountry,pageNumber]);

  const getDepartements = useCallback(
    (region = '', country = '') => {
      httpClient.current?.get(`/api/departement/all`).then((response) => {
        setDepartements(response.data);
        buildData(response.data, search, region, country);
      });
    },
    [httpClient]
  );

  const buildData = (newDepartement, searchCriteria, regionCreteria, countryCreteria) => {
    const pagesVisited = pageNumber * NUMBER_OF_DEPARTEMENTS_PER_PAGE;
    const filteredDepartements = newDepartement.filter((activity) =>
      activity.name.toUpperCase().includes(searchCriteria.toUpperCase())
    );
    let filteredByRegion = countryCreteria
      ? filteredDepartements.filter((departement) => departement.region.country.id == countryCreteria)
      : filteredDepartements;
    filteredByRegion = regionCreteria
      ? filteredByRegion.filter((departement) => departement.region.id == regionCreteria)
      : filteredByRegion;

    const restPagination = filteredByRegion.slice(pagesVisited, pagesVisited + activityPerPage);
    const newData = restPagination.map((departement) => {
      let action = (
        <div>
          <span>
            <EditDepartement departement={departement} departements={getDepartements} />
          </span>
          <span>
            <DeleteButtonIcon
              title={props.t('delete_departement')}
              onClick={() =>
                SweetAlert.fire({
                  title: props.t('you_delete'),
                  text: props.t('not_recover'),
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonText: 'Ok',
                  cancelButtonText: props.t('cancel'),
                  reverseButtons: true,
                }).then((result) => {
                  if (result.value) {
                    deleteDepartement(departement.id);
                    SweetAlert.fire(props.t('Deleted!'), props.t('isDeleted'), 'success');
                  } else {
                    SweetAlert.fire(props.t('isNotDeleted'));
                  }
                })
              }
              className={`fa fa-trash ${styles['trash']}`}
            />
          </span>
        </div>
      );
      let status =
        spinner.state === true && spinner.id === departement.id ? (
          <div className={`loader-box  ${styles['spinner']}`}>
            <div className="loader-2"></div>
          </div>
        ) : (
          <input
            type="checkbox"
            disabled={role === 'admin'}
            name="status"
            checked={departement?.valid}
            onChange={(e) => handleCheckBox(e, departement)}
          />
        );

      return {
        id: departement.id,
        name: departement.name,
        status: status,
        action: action,
      };
    });
    setData(newData);
  };

  const deleteDepartement = async (countryId) => {
    await httpClient.current?.delete(`/api/departement/${countryId}`);
    getDepartements();
  };

  const handleCheckBox = async (event, activity) => {
    setSpinner({ state: true, id: activity.id });
    const activityValidation = { id: activity.id, name: activity.name, valid: event.target.checked };
    const response = httpClient.current?.put(`/api/departement/update`, activityValidation);
    if ((await response).status === 200) {
      getDepartements();
      setSpinner({ state: false, id: activity.id });
    }
  };

  useEffect(() => {
    getDepartements();

    if (keycloak.authenticated) {
      keycloak.hasResourceRole('ROLE_AGRISED_ADMIN_SUPER')
        ? setRole('super admin')
        : keycloak.hasResourceRole('ROLE_AGRISED_ADMIN')
        ? setRole('admin')
        : setRole('user');
    }
    fetchCountries();
  }, [getDepartements, keycloak]);

  useEffect(() => {
    getDepartements(selectedRegion, selectedCountry);
  }, [getDepartements, selectedRegion, selectedCountry,pageNumber]);

  useEffect(() => {
    buildData(departements, search,selectedRegion, selectedCountry);
  }, [departements, search, selectedRegion, selectedCountry, pageNumber]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
    buildData(departements, event.target.value, selectedRegion, selectedCountry);
    changePage({ selected: 0 });
  };
  return (
    <div>
      <Fragment>
        <Breadcrumb parent={props.t('country_url')} url={'settings/departement'} />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <CardHeader>
                  <div className="float-right">
                    <CreateDepartement departements={getDepartements} />
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="d-flex mb-2">
                    <input
                      className="form-control col-md-6 mr-2 "
                      onChange={handleSearch}
                      value={search}
                      placeholder={props.t('search_departments')}
                    />
                    <div className="flex-grow-1">
                      <select
                        className="form-control col-md-6 mb-2"
                        value={selectedCountry}
                        onChange={(event) =>
                          selectedRegion && selectedCountry == ''
                            ? setSelectedRegion('')
                            : setSelectedCountry(event.target.value)
                        }
                      >
                        <option value="">{props.t('all_countries')}</option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      {selectedCountry !== '' && (
                        <select
                          className="form-control col-md-6"
                          value={selectedRegion}
                          onChange={(event) => setSelectedRegion(event.target.value)}
                        >
                          <option value="">{props.t('all_regions')}</option>
                          {regions.map((region) => (
                            <option key={region.id} value={region.id}>
                              {region.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>

                  {data.length === 0 ? (
                    <Alert variant="outlined" severity="info">
                      {props.t('no_country')}
                    </Alert>
                  ) : (
                    <ActivityColumns data={data} />
                  )}
                  <br />
                  <div className="float-right">
                    {departements.length > NUMBER_OF_DEPARTEMENTS_PER_PAGE && (
                      <ReactPaginate
                        previousLabel="<"
                        nextLabel=">"
                        pageCount={pageCount}
                        onPageChange={changePage}
                        containerClassName={styles['paginationButton']}
                        previousLinkClassName={styles['previousBtn']}
                        nextLinkClassName={styles['nextBtn']}
                        disabledClassName={styles['paginationDisabled']}
                        activeClassName={styles['paginationActive']}
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    </div>
  );
};
export default translate(ListDepartementsPage);
